import { createContext, useContext, useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAutoLogout } from '@/hooks/useAutoLogout';

// ... (interface and context creation are the same)
interface AuthContextType {
  user: User | null;
  session: Session | null;
  userRole: string | null;
  userProfile: any;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<any>;
  signUp: (email: string, password: string, displayName: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  isAdmin: () => boolean;
  isSuperAdmin: () => boolean;
  isStaffToko: () => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const ALLOWED_DOMAINS = ['@bazariadigital.id', '@it-bap.club', '@gmail.com', '@bazariadigital.com'];


export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [userProfile, setUserProfile] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  // ==================================================================
  // Auto Logout Hook When User is Inactive & Have a Session Logged In
  // ==================================================================
  useAutoLogout(() => {
    console.warn('[DEBUG] Auto logout triggered due to inactivity.');
    signOut();
    toast({
      title: "Session Expired",
      description: "You have been logged out due to inactivity.",
      variant: "destructive",
    });
  }, 30, !!user); // 30 Minutes timeout

  const validateEmailDomain = (email: string) => {
    const emailDomain = email.substring(email.lastIndexOf('@'));
    return ALLOWED_DOMAINS.includes(emailDomain.toLowerCase());
  };

  // ==================================================================
  // FETCH USER DATA WITH DETAILED LOGGING
  // ==================================================================
  const fetchUserData = async (userId: string) => {
    console.log(`[DEBUG] 1. fetchUserData: START for user ${userId}`);
    try {
      // Fetch user profile
      console.log(`[DEBUG] 2. fetchUserData: Fetching profile...`);
      const { data: profiles, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', userId);
      console.log(`[DEBUG] 3. fetchUserData: Profile result`, { profiles, profileError });

      if (profileError) throw profileError;
      if (!profiles || profiles.length === 0) console.warn(`[DEBUG] No profile found for user ${userId}.`);

      setUserProfile(profiles?.[0] || null);

      // Fetch user role
      console.log(`[DEBUG] 4. fetchUserData: Fetching role...`);
      const { data: roles, error: roleError } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId);
      console.log(`[DEBUG] 5. fetchUserData: Role result`, { roles, roleError });

      if (roleError) throw roleError;
      if (!roles || roles.length === 0) console.warn(`[DEBUG] No role found for user ${userId}.`);

      setUserRole(roles?.[0]?.role || null);
      console.log(`[DEBUG] 6. fetchUserData: END successfully for user ${userId}`);

    } catch (error) {
      setUserProfile(null);
      setUserRole(null);
      console.error(`[DEBUG] 7. fetchUserData: CATCH block`, error);
      toast({
        title: "Gagal memuat data pengguna",
        description: "Terjadi kesalahan saat mengambil data profil Anda.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // ==================================================================
  // USE EFFECT WITH PROPER SESSION HANDLING
  // ==================================================================
  useEffect(() => {
    let isMounted = true;
    console.log('[DEBUG] A. AuthProvider useEffect: MOUNTED. Setting loading = true.');
    setLoading(true);

    let currentSession: Session | null = null;
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (!isMounted) return;
        console.log(`[DEBUG] B. onAuthStateChange: TRIGGERED with event: ${event}`);

        setSession(session);
        setUser(session?.user ?? null);
        currentSession = session;

        if (session?.user) {
          console.log(`[DEBUG] C. onAuthStateChange: User found (${session.user.id}). Fetching user data.`);
          setTimeout(() => {
            if (isMounted) {
              fetchUserData(session.user.id);
            }
          }, 0);
        } else {
          console.log(`[DEBUG] C. onAuthStateChange: No user session found.`);
          setUserProfile(null);
          setUserRole(null);
          if (isMounted) {
            setLoading(false);
          }
        }
      }
    );

    // Interval pengecekan expired session
    const checkSessionInterval = setInterval(() => {
      if (currentSession && currentSession.expires_at) {
        const now = Math.floor(Date.now() / 1000);
        if (now >= currentSession.expires_at) {
          console.warn('[DEBUG] D. Session expired, signing out...');
          signOut();
          toast({
            title: "Session Expired",
            description: "Your session has expired. Please log in again.",
            variant: "destructive",
          });
        }
      }
    }, 60000);

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (isMounted && !session) {
        setLoading(false);
      }
      currentSession = session;
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
      console.log('[DEBUG] G. AuthProvider useEffect: UNMOUNTED.');
      clearInterval(checkSessionInterval);
    };
  }, []);

  // ==================================================================
  // SIGN IN (simplified, lets onAuthStateChange do the work)
  // ==================================================================
  const signIn = async (email: string, password: string) => {
    if (!validateEmailDomain(email)) {
      return { error: { message: `Domain email tidak diizinkan.` } };
    }
    console.log('[DEBUG] signIn: Attempting login...');
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      console.error('[DEBUG] signIn: Login failed.', error);
      toast({ title: "Login Gagal", description: error.message, variant: "destructive" });
    } else {
      console.log('[DEBUG] signIn: Login successful via Supabase. Waiting for onAuthStateChange.');
    }
    return { data, error };
  };

  // ... (signUp and signOut can remain the same)
  const signUp = async (email: string, password: string, displayName: string) => {
    if (!validateEmailDomain(email)) {
      return { error: { message: `Email harus menggunakan domain yang diizinkan: ${ALLOWED_DOMAINS.join(', ')}` } };
    }
    const { error } = await supabase.auth.signUp({ email, password, options: { data: { display_name: displayName } } });
    if (error) {
      toast({ title: "Registrasi Gagal", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Registrasi Berhasil!", description: "Silakan cek email untuk konfirmasi akun" });
    }
    return { error };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null); setSession(null); setUserRole(null); setUserProfile(null);
    toast({ title: "Logout Berhasil" });
  };

  const isAdmin = () => userRole === 'superadmin' || userRole === 'staff_toko';
  const isSuperAdmin = () => userRole === 'superadmin';
  const isStaffToko = () => userRole === 'staff_toko';

  const value = { user, session, userRole, userProfile, loading, signIn, signUp, signOut, isAdmin, isSuperAdmin, isStaffToko };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}